import { useEffect, useRef, useState } from "react";
import { collection, query, orderBy, limit, getDocs } from "firebase/firestore";
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Download, Link } from "lucide-react";
import { db } from "../firebase-config";
import { ordinal } from "../ordinal";
import { assignRanks } from "../rank";
import { getEquippedItem } from "../cosmetics";
import { shareImageToPlatform, SITE_URL } from "../shareImage";
import AvatarRing from "./AvatarRing";
import SubtitleBadge from "./SubtitleBadge";
import ShareableRankCard from "./ShareableRankCard";
import { TRUST_COLOR, TEXT_MUTED, BORDER, TEXT_DARK } from "../theme";

const PAGE_SIZE = 10;

function XIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" style={{ display: "block" }}>
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.746l7.73-8.835L1.254 2.25H8.08l4.259 5.632L18.244 2.25zM17.083 19.77h1.833L7.084 4.126H5.117L17.083 19.77z" />
    </svg>
  );
}
function WhatsAppIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" style={{ display: "block" }}>
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}
function FacebookIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" style={{ display: "block" }}>
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
    </svg>
  );
}

function SharePlatformBtn({ label, icon, bg, onClick, active }) {
  return (
    <button
      onClick={onClick}
      disabled={!!active}
      style={{
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
        gap: "5px", padding: "10px 4px",
        background: bg, color: "#fff", border: "none", borderRadius: "8px",
        fontSize: "0.625rem", fontWeight: "700", opacity: active ? 0.6 : 1,
      }}
    >
      {icon}
      {active ? "…" : label}
    </button>
  );
}

const MONTH_NAMES = ["january", "february", "march", "april", "may", "june", "july", "august", "september", "october", "november", "december"];

function Leaderboard({ onClose, currentUser, userProfile, shopItems = [] }) {
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [page, setPage] = useState(0);
  const [sharing, setSharing] = useState(null);
  const rankCardRef = useRef(null);

  useEffect(() => {
    loadLeaderboard();
  }, [selectedMonth, selectedYear]);

  const loadLeaderboard = async () => {
    setLoading(true);
    try {
      const snapshot = await getDocs(query(collection(db, "users"), orderBy("totalScore", "desc"), limit(100)));
      const monthKey = `${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}`;

      const users = snapshot.docs.map((doc) => {
        const data = doc.data();
        const history = data.gameHistory || {};
        let monthlyScore = 0, monthlyGames = 0;
        Object.entries(history).forEach(([k, g]) => {
          if (k.startsWith(monthKey)) { monthlyScore += g.myScore || 0; monthlyGames++; }
        });
        return {
          uid: doc.id,
          displayName: data.displayName,
          photoURL: data.photoURL,
          totalScore: data.totalScore || 0,
          monthlyScore, monthlyGames,
          gamesPlayed: data.gamesPlayed || 0,
          equipped: data.equipped || null,
        };
      });

      const sorted = users.filter((u) => u.monthlyGames > 0).sort((a, b) => b.monthlyScore - a.monthlyScore);
      const ranked = assignRanks(sorted, "monthlyScore");
      setLeaderboard(ranked);

      const myIndex = ranked.findIndex((u) => u.uid === currentUser?.uid);
      setPage(myIndex >= 0 ? Math.floor(myIndex / PAGE_SIZE) : 0);
    } catch (err) {
      console.error("Error loading leaderboard:", err);
    } finally {
      setLoading(false);
    }
  };

  const changeMonth = (delta) => {
    let m = selectedMonth + delta, y = selectedYear;
    if (m > 11) { m = 0; y++; }
    else if (m < 0) { m = 11; y--; }
    setSelectedMonth(m);
    setSelectedYear(y);
  };

  const isCurrentMonth = selectedMonth === new Date().getMonth() && selectedYear === new Date().getFullYear();
  const currentUserData = leaderboard.find((u) => u.uid === currentUser?.uid);
  const totalPages = Math.max(1, Math.ceil(leaderboard.length / PAGE_SIZE));
  const pageItems = leaderboard.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);
  const monthLabel = `${MONTH_NAMES[selectedMonth]} ${selectedYear}`;

  const doShare = async (platform) => {
    setSharing(platform);
    const shareText = `i'm ranked ${ordinal(currentUserData.rank)} on trust game this month`;
    const result = await shareImageToPlatform(rankCardRef.current, {
      platform,
      text: shareText,
      url: SITE_URL,
    });
    setSharing(null);
    if (!result.ok) alert(result.error);
  };

  const copyLink = () => navigator.clipboard.writeText(SITE_URL).then(() => alert("link copied!"));

  return (
    <div style={{ padding: 20, maxWidth: "640px", margin: "40px auto" }}>
      {/* Off-screen rank card for image capture */}
      {currentUserData && userProfile && (
        <div style={{ position: "fixed", left: "-9999px", top: 0, width: "380px", zIndex: -1 }}>
          <div ref={rankCardRef}>
            <ShareableRankCard
              userProfile={userProfile}
              shopItems={shopItems}
              rank={currentUserData.rank}
              points={currentUserData.monthlyScore}
              games={currentUserData.monthlyGames}
              monthLabel={monthLabel}
            />
          </div>
        </div>
      )}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
        <h2 style={{ fontSize: "1.125rem", fontWeight: "700" }}>leaderboard</h2>
        <button onClick={onClose} style={{ padding: "6px 14px", background: "#fff", color: TEXT_MUTED, border: `1px solid ${BORDER}`, borderRadius: "8px", fontSize: "0.8125rem" }}>close</button>
      </div>

      {/* Month selector */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "14px", padding: "10px 14px", border: `1px solid ${BORDER}`, borderRadius: "10px" }}>
        <button onClick={() => changeMonth(-1)} style={{ background: "none", border: "none", padding: "4px", display: "flex" }}>
          <ChevronLeft size={18} color={TEXT_MUTED} />
        </button>
        <div style={{ fontSize: "0.8125rem", fontWeight: "600" }}>{monthLabel}</div>
        <button onClick={() => changeMonth(1)} disabled={isCurrentMonth} style={{ background: "none", border: "none", padding: "4px", display: "flex", opacity: isCurrentMonth ? 0.3 : 1 }}>
          <ChevronRight size={18} color={TEXT_MUTED} />
        </button>
      </div>

      {/* Current user stats */}
      {currentUserData && (
        <div style={{ border: `1px solid ${TRUST_COLOR}`, padding: "14px", borderRadius: "10px", marginBottom: "14px" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "10px", marginBottom: currentUserData ? "14px" : 0 }}>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: "1.375rem", fontWeight: "700" }}>{ordinal(currentUserData.rank)}</div>
              <div style={{ fontSize: "0.6875rem", color: TEXT_MUTED, marginTop: "4px" }}>rank</div>
            </div>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: "1.375rem", fontWeight: "700", color: TRUST_COLOR }}>{currentUserData.monthlyScore}</div>
              <div style={{ fontSize: "0.6875rem", color: TEXT_MUTED }}>points</div>
            </div>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: "1.375rem", fontWeight: "700" }}>{currentUserData.monthlyGames}</div>
              <div style={{ fontSize: "0.6875rem", color: TEXT_MUTED }}>games</div>
            </div>
          </div>

          {/* Share your rank */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: "8px", marginBottom: "8px" }}>
            <SharePlatformBtn label="tweet"    icon={<XIcon size={15} />}        bg="#000"        onClick={() => doShare("twitter")}  active={sharing === "twitter"} />
            <SharePlatformBtn label="facebook" icon={<FacebookIcon size={15} />} bg="#1877F2"     onClick={() => doShare("facebook")} active={sharing === "facebook"} />
            <SharePlatformBtn label="whatsapp" icon={<WhatsAppIcon size={15} />} bg="#25D366"     onClick={() => doShare("whatsapp")} active={sharing === "whatsapp"} />
            <SharePlatformBtn label="image"    icon={<Download size={15} />}     bg={TRUST_COLOR} onClick={() => doShare("image")}    active={sharing === "image"} />
          </div>
          <button onClick={copyLink} style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: "6px", padding: "8px", fontSize: "0.75rem", fontWeight: "600", background: "#fff", color: TEXT_MUTED, border: `1px solid ${BORDER}`, borderRadius: "8px" }}>
            <Link size={13} /> copy link
          </button>
        </div>
      )}

      {/* Leaderboard table */}
      {loading ? (
        <p style={{ textAlign: "center", padding: "30px", color: TEXT_MUTED, fontSize: "0.8125rem" }}>loading...</p>
      ) : leaderboard.length === 0 ? (
        <p style={{ textAlign: "center", padding: "30px", color: TEXT_MUTED, fontSize: "0.8125rem" }}>no players this month yet</p>
      ) : (
        <>
          <div style={{ background: "white", borderRadius: "10px", overflow: "hidden", border: `1px solid ${BORDER}` }}>
            {pageItems.map((player, i) => {
              const isCurrentUser = player.uid === currentUser?.uid;
              return (
                <div
                  key={player.uid}
                  style={{ display: "grid", gridTemplateColumns: "44px 1fr 60px", alignItems: "center", padding: "12px 14px", borderBottom: i < pageItems.length - 1 ? `1px solid ${BORDER}` : "none", background: isCurrentUser ? "#f3f5ff" : "white" }}
                >
                  <div style={{ fontSize: "0.8125rem", color: TEXT_MUTED, fontWeight: "600" }}>{ordinal(player.rank)}</div>
                  <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <AvatarRing photoURL={player.photoURL} size={34} item={getEquippedItem(player, shopItems, "border")} fallbackBorder={`1px solid ${BORDER}`} />
                    <div>
                      <div style={{ fontSize: "0.8125rem", fontWeight: isCurrentUser ? "700" : "500", display: "flex", alignItems: "center", gap: "6px" }}>
                        {player.displayName}
                        {isCurrentUser && <span style={{ fontSize: "0.6875rem", color: TRUST_COLOR }}>(you)</span>}
                        <SubtitleBadge item={getEquippedItem(player, shopItems, "subtitle")} />
                      </div>
                      <div style={{ fontSize: "0.6875rem", color: TEXT_MUTED }}>{player.monthlyGames} games this month</div>
                    </div>
                  </div>
                  <div style={{ textAlign: "right", fontSize: "1rem", fontWeight: "700" }}>{player.monthlyScore}</div>
                </div>
              );
            })}
          </div>

          {totalPages > 1 && (
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "6px", marginTop: "14px" }}>
              <PageButton onClick={() => setPage(0)} disabled={page === 0}><ChevronsLeft size={16} color={TEXT_MUTED} /></PageButton>
              <PageButton onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}><ChevronLeft size={16} color={TEXT_MUTED} /></PageButton>
              <div style={{ fontSize: "0.75rem", color: TEXT_MUTED, padding: "0 8px" }}>page {page + 1} of {totalPages}</div>
              <PageButton onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}><ChevronRight size={16} color={TEXT_MUTED} /></PageButton>
              <PageButton onClick={() => setPage(totalPages - 1)} disabled={page >= totalPages - 1}><ChevronsRight size={16} color={TEXT_MUTED} /></PageButton>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function PageButton({ onClick, disabled, children }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{ background: "#fff", border: `1px solid ${BORDER}`, borderRadius: "8px", padding: "6px 8px", display: "flex" }}>
      {children}
    </button>
  );
}

export default Leaderboard;
